import { apiFetch } from './config';

// Typed relations written INSIDE prose: `supports::[[target-id|display]]`.
// Slice C of the reader-mint sitting (M-3, driven 2026-08-07).
//
// ── THE RULES THIS OBEYS, AND WHY EACH ONE ─────────────────────────────────
//
// SAME MATCHER, never a fork. The name is resolved through
// GET /api/relations/match — the identical endpoint the pair page's typed field
// uses — so a synonym refused there is refused here, and `crux_of` vs
// `bears_on` direction is decided by one implementation. A second matcher would
// be a second vocabulary, discovered later by someone wondering why the same
// word behaved differently in two boxes.
//
// CONFIRM BEFORE DRAW (the owner's M-3 pick): "the review step is probably good
// since it'll cut down on erroneous relations". Parsing produces PROPOSALS;
// nothing reaches the graph until a person presses the chip.
//
// PARSED ON EVERY SAVE, not only at creation (open question #5, reversed
// 2026-08-07). Create-only meant typing the syntax into an object you were
// EDITING did nothing, forever, leaving it sitting in the text as a dead end
// that teaches nothing. Parsing on edit is safe because of the two guards
// below, and because the confirm step already exists.
//
// DEDUP ON (source, relation, target). Re-saving the same text must not draw
// the same edge twice, and this is the guard that makes parse-on-every-save
// possible at all. Checked against the object's live relations before a chip is
// ever offered — an already-drawn relation simply does not appear as a
// proposal, rather than appearing and failing.
//
// AN UNKNOWN NAME IS REFUSED, never silently downgraded to `related_to`. That
// downgrade is exactly how `related_to` became a dumping ground (DECISIONS.md
// 2026-08-04); the refusal names the word and points at the pair page, where
// minting a relation type is a deliberate act.

const SYNTAX = /(\w[\w-]*)::\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;

/** Every `name::[[target]]` in the text, in order, de-duplicated by pair. */
export function parseProseRelations(text) {
  const seen = new Set();
  const out = [];
  let m;
  SYNTAX.lastIndex = 0;
  while ((m = SYNTAX.exec(String(text ?? '')))) {
    const [, name, target, display] = m;
    const key = `${name.toLowerCase()}|${target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name, target, display: display || target });
  }
  return out;
}

/**
 * Turn parsed mentions into PROPOSALS, dropping any that already exist.
 *
 * `existing` is the object's live relation rows (ObjectDetail already holds
 * them), used for the dedup guard. Each proposal carries the matcher's verdict:
 * `resolved` when a real relation name was found, `refused` when it was not.
 */
export async function resolveProseRelations(sourceId, mentions, existing = []) {
  // Direction is part of the identity of an edge, so the dedup key carries it:
  // `supports` out of this object is a different assertion from `supports`
  // into it, and collapsing them would suppress a legitimate second edge.
  const drawn = new Set([
    ...existing
      .filter((r) => r.source_id === sourceId)
      .map((r) => `${r.relation_name}|out|${r.target_id}`),
    ...existing
      .filter((r) => r.target_id === sourceId)
      .map((r) => `${r.relation_name}|in|${r.source_id}`),
  ]);

  const out = [];
  for (const mention of mentions) {
    let match = null;
    try {
      // a_type/b_type are what let the matcher fix DIRECTION (crux_of runs
      // question → claim while bears_on runs claim → question). Prose does not
      // know the endpoint types, so the matcher answers on the name alone and
      // reports the direction it chose in `source`.
      const params = new URLSearchParams({ q: mention.name });
      const res = await apiFetch(`/api/relations/match?${params.toString()}`);
      if (res.ok) {
        const body = await res.json();
        // The contract is { candidates, create } — NOT a bare array, and not
        // `options`. Reading the wrong key made every name refuse, including
        // `supports` (caught in the drive, 2026-08-07).
        const candidates = body.candidates || [];
        // Only an EXISTING type counts. `create` is the matcher's offer to MINT
        // a new relation name, and that belongs to the pair page where naming
        // one is the deliberate act — not to a save that happened to contain an
        // unfamiliar word.
        match = candidates.slice().sort((x, y) => (y.quality || 0) - (x.quality || 0))[0] || null;
      }
    } catch {
      /* a failed match leaves this mention refused, which is the safe side */
    }
    if (!match) {
      out.push({ ...mention, status: 'refused' });
      continue;
    }
    // `source: 'b'` means the matcher read the phrase as running from the OTHER
    // object back to this one. Honour it, or a name whose natural direction is
    // reversed would be drawn backwards and silently mean the opposite thing.
    const swapped = match.source === 'b';
    const key = swapped
      ? `${match.name}|in|${mention.target}`
      : `${match.name}|out|${mention.target}`;
    if (drawn.has(key)) {
      out.push({ ...mention, status: 'already', resolvedName: match.name, swapped });
      continue;
    }
    out.push({
      ...mention,
      status: 'ready',
      resolvedName: match.name,
      relationId: match.relation_id,
      swapped,
      // the matcher may have resolved a synonym or an inverse label; say so on
      // the chip rather than quietly drawing something the author did not type
      viaSynonym: match.name.toLowerCase() !== mention.name.toLowerCase() ? mention.name : null,
    });
  }
  return out;
}

/** Draw one confirmed proposal: the edge, then the author's +1. */
export async function drawProseRelation(sourceId, proposal) {
  // `swapped` came from the matcher, which decides direction from the name —
  // the same authority RelationField defers to. Drawing it the other way would
  // silently assert the inverse of what was written.
  const from = proposal.swapped ? proposal.target : sourceId;
  const to = proposal.swapped ? sourceId : proposal.target;
  const res = await apiFetch('/api/relation-instances', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      relation_id: proposal.relationId,
      source_id: from,
      target_id: to,
      polarity: 1,
    }),
  });
  const edge = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(edge.error || 'Could not draw that connection');
  // The hand-assertion +1, same as RelationField's commit path: writing
  // "supports::[[x]]" and confirming it IS asserting the relation.
  await apiFetch('/api/votes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ relation_instance_id: edge.id, polarity: 1 }),
  });
  return edge;
}
